<script lang="ts">
  import { addCredits } from '../lib/components/map/credits';
  import { onDestroy, onMount } from 'svelte';
  import * as maplibregl from 'maplibre-gl';
  import { api } from '../lib/api/client';
  import type { Fix, MapInfo } from '../lib/api/types';
  import { age } from '../lib/utils/time';
  import { wakeCause } from '../lib/utils/memberState';
  import { confirm } from '../lib/stores/confirmDialog';
  import { managedBy } from '../lib/stores/auth';
  import { followTheme, northUpMap, styleFor } from '../lib/components/map/theme';
  import Modal from '../lib/components/Modal.svelte';
  import { integer, t } from '../lib/i18n/index.ts';

  // Devices is Find my devices (spec 011): where each of the member's
  // phones, tablets, computers, and trackers last was, on one map, with a day's trail
  // for the one chosen, Locate now, and the member's trackers.

  interface DeviceLocation {
    id: string;
    name: string;
    kind: 'phone' | 'tablet' | 'tracker' | 'computer';
    primary: boolean;
    last_seen_at: string;
    state: 'reporting' | 'permission' | 'quiet';
    reason?: string;
    fix?: Fix;
    locatable: boolean;
    wake_error?: string;
  }
  interface TrackerToken {
    device: { id: string; name: string };
    token: string;
    upload_url: string;
  }

  const kindIcon: Record<string, string> = { phone: 'la-mobile', tablet: 'la-tablet', tracker: 'la-satellite', computer: 'la-laptop' };
  const kindLabel: Record<string, string> = {
    phone: t('devicesKindPhone'),
    tablet: t('devicesKindTablet'),
    tracker: t('devicesKindTracker'),
    computer: t('devicesKindComputer'),
  };
  const locateReason: Record<string, string> = {
    coalesced: t('devicesLocateCoalesced'),
    budget: t('devicesLocateBudget'),
    unreachable: t('devicesLocateUnreachable'),
  };
  // SLOT stands in for the code inside devicesAuthHelp and devicesBatchHelp,
  // which are split around it.
  const SLOT = '\u0001';

  let devices = $state<DeviceLocation[]>([]);
  let loaded = $state(false);
  let error = $state('');
  let selected = $state<string | null>(null);
  let trail = $state<Fix[]>([]);
  let note = $state<Record<string, string>>({});
  let now = $state(Date.now());
  let adding = $state(false);
  let newName = $state('');
  let issued = $state<TrackerToken | null>(null);
  let renaming = $state<DeviceLocation | null>(null);
  let rename = $state('');
  let busy = $state(false);

  let mapEl: HTMLDivElement | undefined = $state();
  let map: maplibregl.Map | null = null;
  let info: MapInfo | null = null;
  let stopTheme: (() => void) | null = null;
  const markers = new Map<string, maplibregl.Marker>();
  let poll: ReturnType<typeof setInterval> | null = null;
  const tick = setInterval(() => (now = Date.now()), 15_000);
  let fitted = false;
  // closed is set once the page is left, so a read still on its way draws
  // nothing on the removed map and starts no polling.
  let closed = false;

  // A child in kids mode sees their devices; their parents add and change
  // trackers (spec 001).
  let locked = $derived($managedBy !== '');

  onMount(async () => {
    // The account page's "Add a GPS tracker" opens the form at once.
    if (new URLSearchParams(window.location.search).get('add') === '1' && !locked) adding = true;
    info = await api.get<MapInfo>('/map').catch(() => null);
    await load();
    if (closed) return;
    // Every 5 seconds while the page shows (011 SC-002), and not while hidden.
    poll = setInterval(() => {
      if (document.visibilityState === 'visible') load();
    }, 5_000);
  });

  onDestroy(() => {
    closed = true;
    if (poll) clearInterval(poll);
    clearInterval(tick);
    stopTheme?.();
    map?.remove();
  });

  async function load() {
    try {
      devices = await api.get<DeviceLocation[]>('/me/device-locations');
      error = '';
      if (selected) trail = await api.get<Fix[]>(`/devices/${selected}/trail`);
    } catch (e: any) {
      error = e.message ?? t('devicesLoadFailed');
    }
    if (closed) return;
    loaded = true;
    draw();
  }

  function ensureMap(): maplibregl.Map | null {
    if (map || !mapEl) return map;
    const first = devices.find((d) => d.fix)?.fix;
    map = northUpMap({
      container: mapEl,
      style: styleFor(info),
      center: first ? [first.lon, first.lat] : [0, 20],
      zoom: first ? 12 : 1,
      attributionControl: false,
    });
    addCredits(map, info, 'other');
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');
    stopTheme = followTheme(map, info, drawTrail);
    return map;
  }

  // draw puts each device with a fix on the map, fitting them all the first
  // time.
  function draw() {
    const m = ensureMap();
    if (!m) return;
    const seen = new Set<string>();
    for (const d of devices) {
      if (!d.fix) continue;
      seen.add(d.id);
      let mk = markers.get(d.id);
      if (!mk) {
        const el = document.createElement('button');
        el.className = 'device-marker';
        el.title = d.name;
        el.innerHTML = `<i aria-hidden="true" class="las ${kindIcon[d.kind] ?? 'la-mobile'}"></i>`;
        el.addEventListener('click', (e) => {
          e.stopPropagation();
          choose(d.id);
        });
        mk = new maplibregl.Marker({ element: el }).setLngLat([d.fix.lon, d.fix.lat]).addTo(m);
        markers.set(d.id, mk);
      } else {
        mk.setLngLat([d.fix.lon, d.fix.lat]);
      }
      mk.getElement().classList.toggle('selected', d.id === selected);
    }
    for (const [id, mk] of markers) {
      if (!seen.has(id)) {
        mk.remove();
        markers.delete(id);
      }
    }
    if (!fitted) {
      const fixes = devices.flatMap((d) => (d.fix ? [d.fix] : []));
      if (fixes.length > 1) {
        const b = new maplibregl.LngLatBounds();
        for (const f of fixes) b.extend([f.lon, f.lat]);
        m.fitBounds(b, { padding: 60, maxZoom: 15, duration: 0 });
      } else if (fixes.length === 1) {
        m.jumpTo({ center: [fixes[0].lon, fixes[0].lat], zoom: 15 });
      }
      fitted = fixes.length > 0;
    }
    drawTrail();
  }

  function drawTrail() {
    if (!map || !map.isStyleLoaded()) return;
    const data: GeoJSON.Feature = {
      type: 'Feature',
      properties: {},
      geometry: { type: 'LineString', coordinates: selected ? trail.map((f) => [f.lon, f.lat]) : [] },
    };
    const src = map.getSource('device-trail') as maplibregl.GeoJSONSource | undefined;
    if (src) {
      src.setData(data);
      return;
    }
    map.addSource('device-trail', { type: 'geojson', data });
    map.addLayer({
      id: 'device-trail',
      type: 'line',
      source: 'device-trail',
      layout: { 'line-join': 'round', 'line-cap': 'round' },
      paint: { 'line-color': '#1e66f5', 'line-width': 4, 'line-opacity': 0.8 },
    });
  }

  async function choose(id: string) {
    if (selected === id) {
      selected = null;
      trail = [];
      draw();
      return;
    }
    selected = id;
    const d = devices.find((x) => x.id === id);
    if (d?.fix) map?.easeTo({ center: [d.fix.lon, d.fix.lat], zoom: Math.max(map.getZoom(), 15), duration: 500 });
    trail = await api.get<Fix[]>(`/devices/${id}/trail`).catch(() => []);
    draw();
  }

  async function locate(d: DeviceLocation) {
    note = { ...note, [d.id]: t('devicesAsking') };
    try {
      const r = await api.post<{ asked: boolean; reason?: string }>(`/devices/${d.id}/locate`);
      let text = r.asked ? t('devicesAsked') : (locateReason[r.reason ?? ''] ?? t('devicesLocateFailed'));
      // unreachable has several causes; the device's wake_error, set by this
      // ask, names which (011 FR-027).
      if (r.reason === 'unreachable') {
        await load();
        const cause = devices.find((x) => x.id === d.id)?.wake_error;
        if (cause) text = t('devicesLocateUnreachableBecause', { cause: wakeCause(cause) });
      }
      note = { ...note, [d.id]: text };
    } catch (e: any) {
      note = { ...note, [d.id]: e.message ?? t('devicesLocateFailed') };
    }
  }

  async function addTracker() {
    busy = true;
    try {
      issued = await api.post<TrackerToken>('/me/trackers', { name: newName.trim() });
      adding = false;
      newName = '';
      await load();
    } catch (e: any) {
      error = e.message ?? t('devicesAddFailed');
    }
    busy = false;
  }

  async function newToken(d: DeviceLocation) {
    if (!(await confirm(t('devicesNewTokenConfirm', { device: d.name })))) return;
    try {
      issued = await api.post<TrackerToken>(`/devices/${d.id}/token`);
    } catch (e: any) {
      error = e.message ?? t('devicesTokenFailed');
    }
  }

  async function saveName() {
    if (!renaming) return;
    busy = true;
    try {
      await api.patch(`/me/trackers/${renaming.id}`, { name: rename.trim() });
      renaming = null;
      await load();
    } catch (e: any) {
      error = e.message ?? t('devicesRenameFailed');
    }
    busy = false;
  }

  async function remove(d: DeviceLocation) {
    if (!(await confirm(t('devicesRemoveConfirm', { device: d.name })))) return;
    try {
      await api.delete(`/devices/${d.id}`);
      if (selected === d.id) {
        selected = null;
        trail = [];
      }
      await load();
    } catch (e: any) {
      error = e.message ?? t('devicesRemoveFailed');
    }
  }

  function copy(text: string) {
    navigator.clipboard?.writeText(text).catch(() => {});
  }

  function stateLine(d: DeviceLocation): string {
    if (d.state === 'permission') return d.reason ? t('devicesCantReportBecause', { reason: d.reason }) : t('devicesCantReport');
    if (d.state === 'reporting') return t('devicesReporting');
    if (d.kind === 'tracker' && !d.fix) return t('devicesNotHeard');
    return t('devicesLastHeard', { age: age(d.last_seen_at, now) });
  }

  function kindLine(d: DeviceLocation): string {
    const kind = kindLabel[d.kind] ?? d.kind;
    return d.primary ? t('devicesKindPrimary', { kind }) : kind;
  }

  function fixLine(fix: Fix): string {
    const args = { age: age(fix.recorded_at, now), accuracy: integer(Math.round(fix.accuracy_m)) };
    if (fix.battery_pct == null) return t('devicesFix', args);
    const battery = integer(fix.battery_pct);
    return fix.charging ? t('devicesFixCharging', { ...args, battery }) : t('devicesFixBattery', { ...args, battery });
  }
</script>

<div class="flex flex-1 min-h-0 flex-col md:flex-row">
  <aside class="md:w-96 shrink-0 border-b md:border-b-0 md:border-r p-4 space-y-3 overflow-y-auto" style="border-color: var(--theme-surface0); background: var(--theme-mantle);">
    <div class="flex items-center justify-between">
      <h1 class="text-xl font-semibold">{t('devicesTitle')}</h1>
      {#if !locked}
        <button class="btn btn-secondary text-sm px-3 py-1" onclick={() => { adding = true; newName = ''; }}>
          <i aria-hidden="true" class="las la-plus mr-1"></i>{t('devicesAddTracker')}
        </button>
      {/if}
    </div>
    <p class="text-sm text-text-muted">{t('devicesOnlyYou')}</p>
    {#if error}<p class="text-sm" style="color: var(--theme-error);">{error}</p>{/if}
    {#if !loaded}
      <p class="text-text-muted">{t('devicesLoading')}</p>
    {/if}
    {#each devices as d (d.id)}
      <div
        class="card p-3 space-y-1 cursor-pointer"
        style={d.id === selected ? 'outline: 2px solid var(--theme-accent);' : ''}
        role="button"
        tabindex="0"
        onclick={() => choose(d.id)}
        onkeydown={(e: KeyboardEvent) => { if (e.key === 'Enter') choose(d.id); }}
      >
        <div class="flex items-center gap-2">
          <i aria-hidden="true" class="las {kindIcon[d.kind] ?? 'la-mobile'} text-xl"></i>
          <span class="font-semibold">{d.name}</span>
          <span class="text-xs text-text-muted">{kindLine(d)}</span>
        </div>
        {#key now}
          {#if d.fix}
            <p class="text-sm">{fixLine(d.fix)}</p>
          {:else}
            <p class="text-sm text-text-muted">{t('devicesNoLocation')}</p>
          {/if}
          <p class="text-xs" style={d.state === 'permission' ? 'color: var(--theme-error);' : ''}>{stateLine(d)}</p>
        {/key}
        {#if note[d.id]}<p class="text-xs text-text-muted">{note[d.id]}</p>{/if}
        {#if d.wake_error && !note[d.id]}<p class="text-xs text-text-muted">{t('devicesLastAsk', { cause: wakeCause(d.wake_error) })}</p>{/if}
        <!-- svelte-ignore a11y_click_events_have_key_events, a11y_no_static_element_interactions -->
        <div class="flex flex-wrap gap-2 pt-1" onclick={(e: MouseEvent) => e.stopPropagation()}>
          {#if d.kind !== 'tracker'}
            <button class="btn btn-secondary text-xs px-2 py-1" disabled={!d.locatable} title={d.locatable ? '' : t('devicesCannotWake')} onclick={() => locate(d)}>
              <i aria-hidden="true" class="las la-crosshairs mr-1"></i>{t('devicesLocate')}
            </button>
          {:else if !locked}
            <button class="btn btn-secondary text-xs px-2 py-1" onclick={() => { renaming = d; rename = d.name; }}>{t('devicesRename')}</button>
            <button class="btn btn-secondary text-xs px-2 py-1" onclick={() => newToken(d)}>{t('devicesNewToken')}</button>
            <button class="btn btn-secondary text-xs px-2 py-1" style="color: var(--theme-error);" onclick={() => remove(d)}>{t('appRemove')}</button>
          {/if}
        </div>
      </div>
    {/each}
  </aside>
  <div class="flex-1 min-h-[50vh] relative">
    <div bind:this={mapEl} class="absolute inset-0"></div>
  </div>
</div>

{#if adding}
  <Modal title={t('devicesAddTracker')} onclose={() => (adding = false)}>
    <form class="space-y-3" onsubmit={(e: SubmitEvent) => { e.preventDefault(); addTracker(); }}>
      <p class="text-sm text-text-muted">{t('devicesTrackerAbout')}</p>
      <label class="block text-sm">{t('devicesName')}
        <input class="input w-full mt-1" maxlength="64" placeholder={t('devicesNameHint')} bind:value={newName} />
      </label>
      <div class="flex justify-end gap-2">
        <button type="button" class="btn btn-secondary" onclick={() => (adding = false)}>{t('appCancel')}</button>
        <button type="submit" class="btn btn-primary" disabled={busy || !newName.trim()}>{t('appAdd')}</button>
      </div>
    </form>
  </Modal>
{/if}

{#if issued}
  {@const auth = t('devicesAuthHelp', { header: SLOT }).split(SLOT)}
  {@const batch = t('devicesBatchHelp', { max: integer(1000), field: SLOT }).split(SLOT)}
  <Modal title={t('devicesSetUp', { device: issued.device.name })} onclose={() => (issued = null)} maxWidth="lg">
    <div class="space-y-3 text-sm">
      <p>{t('devicesSetUpIntro')}</p>
      <div>
        <div class="text-text-muted">{t('devicesUrl')}</div>
        <div class="flex gap-2 items-center"><code class="break-all">{issued.upload_url}</code><button class="btn btn-secondary text-xs px-2 py-1" onclick={() => copy(issued!.upload_url)}>{t('appCopy')}</button></div>
      </div>
      <div>
        <div class="text-text-muted">{t('devicesToken')}</div>
        <div class="flex gap-2 items-center"><code class="break-all">{issued.token}</code><button class="btn btn-secondary text-xs px-2 py-1" onclick={() => copy(issued!.token)}>{t('appCopy')}</button></div>
      </div>
      <p class="text-text-muted">{auth[0]}<code>Authorization: Bearer</code>{auth[1] ?? ''} {batch[0]}<code>tst</code>{batch[1] ?? ''}</p>
      <div class="flex justify-end"><button class="btn btn-primary" onclick={() => (issued = null)}>{t('appDone')}</button></div>
    </div>
  </Modal>
{/if}

{#if renaming}
  <Modal title={t('devicesRenameTitle', { device: renaming.name })} onclose={() => (renaming = null)}>
    <form class="space-y-3" onsubmit={(e: SubmitEvent) => { e.preventDefault(); saveName(); }}>
      <input class="input w-full" maxlength="64" bind:value={rename} />
      <div class="flex justify-end gap-2">
        <button type="button" class="btn btn-secondary" onclick={() => (renaming = null)}>{t('appCancel')}</button>
        <button type="submit" class="btn btn-primary" disabled={busy || !rename.trim()}>{t('appSave')}</button>
      </div>
    </form>
  </Modal>
{/if}

<style>
  :global(.device-marker) {
    width: 34px;
    height: 34px;
    border-radius: 9999px;
    background: var(--theme-accent, #1e66f5);
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    border: 2px solid white;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.4);
    cursor: pointer;
  }
  :global(.device-marker.selected) {
    outline: 3px solid var(--theme-warning, #df8e1d);
  }
</style>
